import { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import Breadcrumb from '@/components/common/Breadcrumb';

interface FormData {
  name: string;
  email: string;
  phone: string;
  inquiryType: string;
  message: string;
  referenceId: string;
}

const ContactUs = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  const [formData, setFormData] = useState<FormData>({
    name: '',
    email: '',
    phone: '',
    inquiryType: '',
    message: '',
    referenceId: '',
  });

  const inquiryTypes = [
    { id: 'general', label: 'General Inquiry', description: 'General questions and feedback' },
    { id: 'services', label: 'Services', description: 'Questions about paid services (Guide, Meeting, Consultation)' },
    { id: 'hospital', label: 'Hospital Information', description: 'Questions about hospitals or doctors' },
    { id: 'visa', label: 'Visa & Travel', description: 'Visa requirements and travel guidance' },
    { id: 'booking', label: 'Booking & Payment', description: 'Payment issues or booking problems' },
    { id: 'other', label: 'Other', description: 'Other issues not listed above' },
  ];

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!formData.name || !formData.email || !formData.inquiryType || !formData.message) {
      setError('Please fill in all required fields.');
      return;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.email)) {
      setError('Please enter a valid email address.');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const response = await fetch('/api/submit-support', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || 'Failed to submit support request');
      }

      setSuccess(true);

      setTimeout(() => {
        setSuccess(false);
        navigate('/support-confirmation');
      }, 2000);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to submit support request. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="container-custom max-w-3xl">
      <Breadcrumb
        items={[
          { label: 'Home', href: '/' },
          { label: 'Contact Us' }
        ]}
      />

      <div className="mb-8">
        <h1 className="text-3xl md:text-4xl font-bold mb-4">
          Contact Us
        </h1>
        <p className="text-lg text-gray-600">
          We're here to help. Send us a message and we'll get back to you within 24 hours.
        </p>
      </div>

      <div className="grid md:grid-cols-3 gap-6 mb-8">
        <div className="bg-white border border-gray-200 rounded-lg p-6">
          <div className="text-3xl mb-3">📧</div>
          <h3 className="font-bold text-gray-900 mb-2">Email Us</h3>
          <p className="text-sm text-gray-600">
            General inquiries and support requests
          </p>
        </div>
        <div className="bg-white border border-gray-200 rounded-lg p-6">
          <div className="text-3xl mb-3">⏰</div>
          <h3 className="font-bold text-gray-900 mb-2">Response Time</h3>
          <p className="text-sm text-gray-600">
            Usually within 24 hours
          </p>
        </div>
        <div className="bg-white border border-gray-200 rounded-lg p-6">
          <div className="text-3xl mb-3">🌍</div>
          <h3 className="font-bold text-gray-900 mb-2">Languages</h3>
          <p className="text-sm text-gray-600">
            English & Chinese support available
          </p>
        </div>
      </div>

      {error && (
        <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg">
          <p className="text-red-800">{error}</p>
        </div>
      )}

      {success ? (
        <div className="mb-6 p-6 bg-green-50 border border-green-200 rounded-lg text-center">
          <svg className="w-16 h-16 mx-auto mb-4 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          <h3 className="text-xl font-bold text-green-800 mb-2">Message Sent!</h3>
          <p className="text-green-700">Redirecting to confirmation page...</p>
        </div>
      ) : (
        <form onSubmit={handleSubmit} className="bg-white border border-gray-200 rounded-lg p-6 md:p-8">
          <h2 className="text-xl font-bold mb-6">Send Us a Message</h2>

          <div className="space-y-6">
            <div>
              <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-2">
                Full Name <span className="text-red-600">*</span>
              </label>
              <input
                id="name"
                type="text"
                name="name"
                value={formData.name}
                onChange={handleInputChange}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                placeholder="Your full name"
                required
              />
            </div>

            <div className="grid md:grid-cols-2 gap-6">
              <div>
                <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
                  Email Address <span className="text-red-600">*</span>
                </label>
                <input
                  id="email"
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  placeholder="your@email.com"
                  required
                />
              </div>
              <div>
                <label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-2">
                  Phone Number <span className="text-gray-500">(Optional)</span>
                </label>
                <input
                  id="phone"
                  type="tel"
                  name="phone"
                  value={formData.phone}
                  onChange={handleInputChange}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  placeholder="+1 234 567 8900"
                />
              </div>
            </div>

            <div>
              <label htmlFor="inquiryType" className="block text-sm font-medium text-gray-700 mb-2">
                How Can We Help? <span className="text-red-600">*</span>
              </label>
              <select
                id="inquiryType"
                name="inquiryType"
                value={formData.inquiryType}
                onChange={handleInputChange}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-white"
                required
              >
                <option value="">Select an option...</option>
                {inquiryTypes.map(type => (
                  <option key={type.id} value={type.id}>
                    {type.label} - {type.description}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label htmlFor="message" className="block text-sm font-medium text-gray-700 mb-2">
                Message <span className="text-red-600">*</span>
              </label>
              <textarea
                id="message"
                name="message"
                value={formData.message}
                onChange={handleInputChange}
                rows={6}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent resize-y"
                placeholder="Please describe your question or issue in detail..."
                required
              />
            </div>

            <div>
              <label htmlFor="referenceId" className="block text-sm font-medium text-gray-700 mb-2">
                Reference ID <span className="text-gray-500">(Optional)</span>
              </label>
              <input
                id="referenceId"
                type="text"
                name="referenceId"
                value={formData.referenceId}
                onChange={handleInputChange}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                placeholder="e.g., MT-12345, Booking #, or Order #"
              />
              <p className="mt-1 text-xs text-gray-500">
                Include reference numbers from previous transactions for faster assistance.
              </p>
            </div>
          </div>

          <div className="mt-8">
            <button
              type="submit"
              disabled={loading}
              className="w-full btn-primary"
            >
              {loading ? 'Sending...' : 'Send Message'}
            </button>
          </div>

          <div className="mt-6 p-4 bg-gray-50 rounded-lg">
            <p className="text-sm text-gray-700">
              <strong>Before you contact us:</strong> Check our{' '}
              <Link to="/faq" className="text-primary-600 hover:text-primary-700 underline">
                FAQ page
              </Link>{' '}
              for answers to common questions about hospitals, visas, and services.
            </p>
          </div>
        </form>
      )}
    </div>
  );
};

export default ContactUs;
